import React, { useState, useMemo, useContext } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Paper, Typography, useTheme } from '@material-ui/core';
import { FormProvider, useForm } from 'react-hook-form';

import { FormDoc, FormSummary as FormSummaryType, User } from '@vyce/core/src/types';
import { DeviceContext } from '@vyce/core/src/contexts';
import { AppSelect } from '@vyce/core/src/components/inputs/AppSelect';

import { FormSummaryTitle } from './components/FormSummaryTitle';
import { FormSummaryToolbar } from './components/FormSummaryToolbar';
import { FormSummaryPoweredByScreen } from './components/FormSummaryPoweredBy';
import { FormPrintLayout } from './components/FormPrintLayout';
import { FormAnswer } from './components/FormAnswer';
import { FormResultSingleChoice } from './components/FormResultSingleChoice';
import { FormResultMultipleChoice } from './components/FormResultMultipleChoice';
import { FormResultText } from './components/FormResultText';
import { FormResultDate } from './components/FormResultDate';
import { FormResultLinearScale } from './components/FormResultLinearScale';
import { FormResultPhoto } from './components/FormResultPhoto';
import { buildFormSummaryTitleProps } from './utils/formSummaryTitleProps';
import { printFormAsPdf } from './utils/formPrintExport';

export const FormResultByQuestion: React.FC<{
  form: FormDoc | null;
  formSummary: FormSummaryType | null;
  user: User;
  companyLogoUrl?: string | null;
}> = ({ form, formSummary, user, companyLogoUrl }) => {
  const theme = useTheme();
  const { isMobile } = useContext(DeviceContext);
  const [questionId, setQuestionId] = useState<string | null>(null);

  const methods = useForm<any>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {},
  });

  const onQuestionIdChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    setQuestionId(event.target.value as string);
  };

  const questionIndex = useMemo(() => {
    return form?.questions?.findIndex(question => question.id === questionId) || 0;
  }, [questionId, form?.questions]);

  const question = useMemo(() => {
    return form?.questions?.find(question => question.id === questionId);
  }, [questionId, form?.questions]);

  const totalAnswers = useMemo(() => {
    return (
      formSummary?.questionSummaries?.find(question => question.questionId === questionId)?.totalAnswers || 0
    );
  }, [questionId, formSummary]);

  const questionSummary = useMemo(() => {
    return formSummary?.questionSummaries?.find(question => question.questionId === questionId);
  }, [questionId, formSummary]);

  return (
    <FormPrintLayout
      companyLogoUrl={companyLogoUrl}
      toolbar={
        questionId ? <FormSummaryToolbar isMobile={isMobile} onExportPdf={printFormAsPdf} /> : undefined
      }
    >
      <FormSummaryTitle {...buildFormSummaryTitleProps(form, user)} />

      <Paper variant="outlined" style={{ padding: 24, paddingTop: 0, width: '100%' }}>
        <Box className="no-print">
          <FormControl style={{ marginTop: 24 }} fullWidth variant="filled">
            <InputLabel id="question">Question</InputLabel>
            <AppSelect labelId="question" fullWidth value={questionId} onChange={onQuestionIdChange}>
              {form?.questions?.map(question => (
                <MenuItem key={question.id} value={question.id}>
                  <span>{question.title}</span>
                </MenuItem>
              ))}
            </AppSelect>
          </FormControl>
        </Box>

        {questionId && (
          <Box>
            <Typography variant="body1" color="textSecondary">
              Question <strong>{questionIndex + 1}</strong> out of <strong>{form?.questions?.length}</strong>
              , {totalAnswers} responses gathered
            </Typography>

            <FormProvider {...methods}>
              <form style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {question && (
                  <Box
                    style={{
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 16,
                      marginTop: 24,
                    }}
                  >
                    <FormAnswer question={question} disabled={true} />
                  </Box>
                )}
              </form>
            </FormProvider>
          </Box>
        )}
      </Paper>

      {questionId && (
        <Box width="100%">
          {question?.answer.type === 'single_choice' && (
            <FormResultSingleChoice question={question} visualization={questionSummary?.visualization} />
          )}
          {question?.answer.type === 'multiple_choice' && (
            <FormResultMultipleChoice question={question} visualization={questionSummary?.visualization} />
          )}
          {(question?.answer.type === 'short_text' || question?.answer.type === 'long_text') && (
            <FormResultText question={question} visualization={questionSummary?.visualization} />
          )}
          {(question?.answer.type === 'date' || question?.answer.type === 'time') && (
            <FormResultDate
              type={question?.answer.type}
              question={question}
              visualization={questionSummary?.visualization}
            />
          )}
          {question?.answer.type === 'linear_scale' && (
            <FormResultLinearScale
              question={question}
              visualization={questionSummary?.visualization}
              totalAnswers={totalAnswers}
            />
          )}
          {question?.answer.type === 'photo' && (
            <FormResultPhoto question={question} visualization={questionSummary?.visualization} />
          )}
        </Box>
      )}

      <FormSummaryPoweredByScreen />
    </FormPrintLayout>
  );
};
